<template>
  <Teleport to="body">
    <TransitionRoot as="template" :show="show" :unmount="unmount">
      <Dialog
        as="div"
        class="relative z-50"
        :unmount="unmount"
        :initial-focus="initialFocus"
        @close="emit('close')"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
          :unmount="unmount"
        >
          <div class="fixed inset-0 bg-fp-gray-5/75 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div data-testid="modal-position" :class="positionClass">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              :unmount="unmount"
            >
              <DialogPanel
                :class="[
                  maxWidthClass,
                  backgroundColourClass,
                  panelPaddingClass,
                  allowContentOverflow ? 'overflow-visible' : 'overflow-hidden',
                ]"
                class="relative transform rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full"
              >
                <DialogTitle v-if="title && hideHeader" as="h3" class="sr-only">
                  {{ title }}
                </DialogTitle>
                <div v-if="showHeader" class="flex items-start gap-3">
                  <div v-if="iconType !== 'none'" class="shrink-0 pt-0.5">
                    <ExclamationTriangleIcon
                      v-if="iconType === 'danger'"
                      class="h-6 w-6 text-fp-red-6"
                      aria-hidden="true"
                    />
                    <LockClosedIcon
                      v-else-if="iconType === 'lock'"
                      class="h-6 w-6 text-fp-green-5"
                      aria-hidden="true"
                    />
                    <ClockIcon
                      v-else-if="iconType === 'clock'"
                      class="h-6 w-6 text-fp-green-5"
                      aria-hidden="true"
                    />
                    <ArrowPathIcon
                      v-else-if="iconType === 'arrow-path'"
                      class="h-6 w-6 text-fp-green-5"
                      aria-hidden="true"
                    />
                    <CheckIcon
                      v-else
                      class="h-6 w-6 text-fp-green-5"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="min-w-0 flex-1 text-left">
                    <slot name="header">
                      <DialogTitle
                        v-if="title"
                        as="h3"
                        class="text-lg font-medium leading-6 text-fp-gray-6 font-public-sans"
                      >
                        {{ title }}
                      </DialogTitle>
                    </slot>
                  </div>
                  <button
                    v-if="showCloseBtn"
                    type="button"
                    :class="backgroundColourClass"
                    class="shrink-0 rounded-md text-fp-gray-4 hover:text-fp-gray-5 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-fp-gray-5 focus-visible:ring-offset-2"
                    @click="emit('close')"
                  >
                    <span class="sr-only">Close</span>
                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div class="text-start">
                  <slot />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/vue/24/outline'

export type ModalIconType =
  | 'success'
  | 'danger'
  | 'lock'
  | 'none'
  | 'clock'
  | 'arrow-path'

const props = withDefaults(
  defineProps<{
    show?: boolean
    title?: string
    iconType?: ModalIconType
    showCloseBtn?: boolean
    hideHeader?: boolean
    maxWidthClass?: string
    backgroundColourClass?: string
    panelPaddingClass?: string
    allowContentOverflow?: boolean
    unmount?: boolean
    initialFocus?: HTMLElement | null
    align?: 'center' | 'top'
  }>(),
  {
    show: false,
    title: '',
    iconType: 'success',
    showCloseBtn: true,
    hideHeader: false,
    maxWidthClass: 'sm:max-w-md',
    backgroundColourClass: 'bg-white',
    panelPaddingClass: 'px-4 pt-5 pb-4 sm:p-6',
    allowContentOverflow: false,
    unmount: true,
    initialFocus: null,
    align: 'center',
  }
)

const emit = defineEmits<{
  (event: 'close'): void
}>()

const slots = useSlots()

const showHeader = computed(
  () =>
    !props.hideHeader &&
    (Boolean(props.title) ||
      Boolean(slots.header) ||
      props.iconType !== 'none' ||
      props.showCloseBtn)
)

const positionClass = computed(() =>
  props.align === 'top'
    ? 'flex min-h-full items-start justify-center p-4 text-center sm:p-0 sm:pt-24'
    : 'flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'
)
</script>
